import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useApplication from '@/hooks/useApplication';
import { ApprovalFailure, MassApproveResponse } from '@/models/MassApproveResponse';

export default function MassApprovalModal({
  open,
  onClose,
  applicationIds,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  applicationIds: number[];
  onSuccess: (approvedIds: number[]) => void;
}) {
  const { setAlert } = useContext(SnackbarContext);
  const { massApproveApplications } = useApplication();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<MassApproveResponse | null>(null);

  const count = applicationIds.length;

  const handleContinue = async () => {
    setSubmitting(true);
    try {
      const response = await massApproveApplications(applicationIds);
      setResult(response);
      if (response.failureCount === 0) {
        setAlert({ severity: 'success', message: `Successfully approved ${response.successCount} application${response.successCount !== 1 ? 's' : ''}.` });
        onSuccess(applicationIds);
        onClose();
      } else if (response.successCount > 0) {
        setAlert({ severity: 'warning', message: `${response.successCount} approved, ${response.failureCount} failed.` });
        onSuccess(applicationIds.filter((id) => !response.failures.some((f) => f.applicationId === id)));
      }
    } catch {
      setAlert({ severity: 'error', message: 'An error occurred while approving applications.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setResult(null);
      onClose();
    }
  };

  const hasFailures = result != null && result.failureCount > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitleWithClose onClose={handleClose}>Approve Applications</DialogTitleWithClose>
      <DialogContent>
        {!hasFailures ? (
          <>
            <Typography>
              By continuing, <strong>{count}</strong> {count === 1 ? 'application' : 'applications'}{' '}
              will be approved and emails will be sent notifying users that their application has been
              approved.
            </Typography>
            <Alert severity='warning' sx={{ mt: 2 }}>
              This action cannot be undone.
            </Alert>
          </>
        ) : (
          <>
            {result.successCount > 0 && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                {result.successCount} application{result.successCount !== 1 ? 's' : ''} approved successfully. {result.failureCount} failed.
              </Alert>
            )}
            {result.successCount === 0 && (
              <Alert severity='error' sx={{ mb: 2 }}>
                All {result.failureCount} application{result.failureCount !== 1 ? 's' : ''} failed to approve.
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {result.failures.map((failure: ApprovalFailure) => (
                <Box
                  key={failure.applicationId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto auto 1fr',
                    gap: 2,
                    alignItems: 'start',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 1,
                  }}
                >
                  <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                    <strong>ID:</strong> {failure.applicationId}
                  </Typography>
                  <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                    <strong>Partner:</strong> {failure.partnerName}
                  </Typography>
                  <Typography variant='body2' color='error' sx={{ wordBreak: 'break-word' }}>
                    {failure.errorMessage}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='secondary' size='large' onClick={handleClose} disabled={submitting}>
          {hasFailures ? 'Close' : 'Cancel'}
        </Button>
        {!hasFailures && (
          <Button
            variant='primary'
            size='large'
            onClick={handleContinue}
            disabled={submitting || count === 0}
          >
            {submitting ? (
              <>
                <span>Approving...</span>
                <CircularProgress size='16px' sx={{ ml: 1, color: 'grey.500' }} />
              </>
            ) : (
              'Continue'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
